import { PriceProvider } from '@ecomm/data-price'
import { Footer } from '@ecomm/footer-components'
import { useFooterQuery } from '@ecomm/footer-data'
import { BelowTheFold } from '@ecomm/lander'
import { PartnerBanner } from '@ecomm/promotions-components'
import {
  CountryRedirectModal,
  PopupQuoteWizard,
  V2HeroBanner
} from '@ecomm/shared-components'
import { useNinetailed } from '@ecomm/shared-ninetailed'
import { TrackingProvider } from '@ecomm/tracking'
import { type Locale, SEO } from '@ecomm/utils'
import type { PageProps } from 'gatsby'
import { Suspense, useEffect, useMemo } from 'react'

import { useExitIntent } from '@ecomm/modal'
import { parsePartnerPageData } from '@ecomm/partners-data'
import { getSeoDescription } from '@ecomm/partners-utils'
import { getFirstPackageUrl } from '@ecomm/partners-utils/getFirstPackageUrl'
import {
  PartnerHeader,
  PartnerPageBrandBanner
} from '@ecomm/partners/components'
import { TermsAndConditions } from '@ecomm/partners/components/TermsAndConditions'
import { StrategicReferral } from '../components/StrategicReferral'
import { usePartnerPageBodyQuery } from '../hooks/usePartnerPageBodyQuery'
import { usePartnerPagePromoData } from '../hooks/usePartnerPagePromoData'
import { usePartnersDataQueries } from '../hooks/usePartnersDataQueries'

export type PageContext = {
  readonly locale: Locale
  readonly contentful_id: string
}

export type Props = Pick<
  PageProps<unknown, PageContext, unknown, Response>,
  'data' | 'location' | 'pageContext' | 'path'
>

function Content({
  path: pagePath,
  pageContext: { locale, contentful_id },
  location
}: Props) {
  const { partnerPageData, quoteWizard } = usePartnersDataQueries(contentful_id)
  const {
    pageTitle,
    partnerName,
    heroBanner,
    heroImage,
    heroText,
    coBrandLogo,
    coBrandTitle,
    termsAndConditionsPosition,
    termsAndConditions,
    termsAndConditionsTitle,
    partnerGroup,
    content,
    header,
    footer: {
      sys: { id: footerId }
    },
    showPartnerPackageAbsoluteDiscountAsRelative
  } = parsePartnerPageData(partnerPageData)

  const footer = useFooterQuery(footerId)

  const { identify } = useNinetailed()

  const id = new URLSearchParams(location.search).get('id')

  const { hasPartnerBanner } = usePartnerPagePromoData({
    partnerName,
    partnerGroup,
    pagePath,
    id
  })

  // This page takes responsibility for setting the partner trait.
  // Relying only on setting the partner cookie will lead to race conditions
  // with NT onRouteChange handler.
  useEffect(() => {
    const timer = setTimeout(() => {
      identify('', { isPartnerCookieSet: true })
    }, 750)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  const packagesData = useMemo(
    () => content.items.flatMap(item => item.contentCollection.items),
    [content]
  )
  const packageSkus = useMemo(
    () => packagesData.map(partnerPackage => partnerPackage.sku),
    [packagesData]
  )
  const bodyContent = usePartnerPageBodyQuery()

  // ECP-12041 experiment
  useExitIntent()

  return (
    <TrackingProvider metaTitle={pageTitle}>
      <PriceProvider
        locale={locale}
        partnerGroup={partnerGroup}
        partnerName={partnerName}
        skus={packageSkus}
      >
        <SEO
          isNofollow={true}
          isNoindex={true}
          lang={locale}
          metaDescription={getSeoDescription(partnerName)}
          metaTitle={pageTitle}
        />
        <CountryRedirectModal />
        <PopupQuoteWizard data={bodyContent.quoteWizard.quoteWizard} />
        {hasPartnerBanner ? <PartnerBanner /> : null}
        <PartnerHeader
          {...header}
          locale={locale}
          partnerName={partnerName}
          quoteWizard={quoteWizard.quoteWizard}
          showShopCTAUrl={getFirstPackageUrl(packagesData)}
        />
        <main
          className="prose md:prose-md lg:prose-lg relative z-0 w-full whitespace-pre-line"
          id="content"
        >
          {heroBanner ? (
            <V2HeroBanner className="-mb-10" {...heroBanner} />
          ) : null}
          <div className="max-w-8xl mx-auto mb-16 flex flex-col gap-16 px-4 pt-8 md:px-8">
            {heroBanner ? null : (
              <PartnerPageBrandBanner
                body={heroText}
                coBrandLogo={coBrandLogo}
                coBrandTitle={coBrandTitle}
                img={heroImage}
                termsAndConditions={termsAndConditions}
                termsAndConditionsPosition={
                  termsAndConditionsPosition !== 'Bottom'
                    ? termsAndConditionsPosition
                    : 'None'
                }
                termsAndConditionsTitle={termsAndConditionsTitle}
              />
            )}
            {(partnerGroup === 'partner-strategic-referral' &&
              locale === 'en-US') ||
            (partnerName === 'coop' && locale === 'en-GB') ? (
              <StrategicReferral
                packagesData={packagesData}
                partnerName={partnerName}
                showPartnerPackageAbsoluteDiscountAsRelative={
                  !!showPartnerPackageAbsoluteDiscountAsRelative
                }
              />
            ) : (
              <BelowTheFold data={bodyContent} />
            )}
            {termsAndConditions && termsAndConditionsPosition === 'Bottom' ? (
              <TermsAndConditions
                bodyClassNames="[&_sup]:text-[10px]"
                showMoreBtnVisible={locale === 'en-GB'}
                termsAndConditions={termsAndConditions}
                termsAndConditionsTitle={termsAndConditionsTitle ?? ''}
              />
            ) : null}
          </div>
        </main>
        <Footer {...footer} />
      </PriceProvider>
    </TrackingProvider>
  )
}

export default function PartnerPageTemplate(props: Props) {
  return (
    <Suspense>
      <Content {...props} />
    </Suspense>
  )
}
